import React, { useEffect, useRef, useState } from 'react';
import { ApplicationIcon, Icon } from '@saviynt/design-system';
import { hierarchy, tree } from 'd3-hierarchy';
import { type Selection } from 'd3-selection';
import { select } from 'd3-selection';
import { timer } from 'd3-timer';

import { type NodeData } from './GraphData';

import './SemicircularGraph.css';

interface SemicircularGraphProps {
  data: NodeData;
  onBack: () => void;
  onNodeClick: (node: NodeData) => void;
}

/**
 * Semicircular Graph
 */
function SemicircularGraph({
  data,
  onBack,
  onNodeClick,
}: SemicircularGraphProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [nodePositions, setNodePositions] = useState<
    { x: number; y: number; data: NodeData }[]
  >([]);
  const [redEdgeIcons, setRedEdgeIcons] = useState<{ x: number; y: number }[]>(
    []
  );
  const [solidEdgeIcons, setSolidEdgeIcons] = useState<
    { x: number; y: number }[]
  >([]);

  useEffect(() => {
    const iconRadius = 30;
    const radius = 350;
    const width = 1000;
    const height = 600;

    const svg = select(svgRef.current);

    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height + 100);

    const g = svg
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height - 100})`);

    const root = hierarchy<NodeData>(data);
    const treeLayout = tree<NodeData>().size([Math.PI + 1.05, radius]);
    const rotationOffset = Math.PI / 6;

    treeLayout(root);

    root.descendants().forEach((d) => {
      const updated = d;

      updated.x! -= rotationOffset;

      if (updated.x != null) {
        updated.x += Math.PI;
      }

      if (updated.depth === 1) {
        updated.y = radius * 0.4;
      }

      if (updated.depth === 2 && updated.parent?.children) {
        const siblings = updated.parent.children;
        const index = siblings.indexOf(updated);
        const count = siblings.length;

        const min = 0.7;
        const max = 1.3;

        const normalizedIndex = (index - Math.floor(count / 2)) / (count / 2);
        const radiusFactor = Math.exp(-(normalizedIndex ** 2));

        updated.y = radius * (min + (max - min) * radiusFactor);
      }
    });

    const radialPoint = (angle: number, r: number): [number, number] => [
      r * Math.cos(angle),
      r * Math.sin(angle),
    ];

    // Dotted Edges
    g.selectAll('path.dotted-link')
      .data(root.links().filter((d) => d.target.data.type === 'dotted'))
      .enter()
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', '#8C9DB8')
      .attr('stroke-dasharray', '4,4')
      .attr('stroke-width', 2)
      .attr('d', (d) => {
        const [sx, sy] = radialPoint(d.source.x!, d.source.y!);
        const [tx, ty] = radialPoint(d.target.x!, d.target.y!);
        const dx = tx - sx;
        const dy = ty - sy;
        const len = Math.sqrt(dx * dx + dy * dy);
        const offsetX = (dx / len) * iconRadius;
        const offsetY = (dy / len) * iconRadius;

        return `M${sx + offsetX},${sy + offsetY} L${tx - offsetX},${
          ty - offsetY
        }`;
      });

    // Solid Edges with No icon in middle
    g.selectAll('path.solid-no-link')
      .data(root.links().filter((d) => d.target.data.type === 'solidNoLink'))
      .enter()
      .append('path')
      .attr('class', 'solid-no-link')
      .attr('fill', 'none')
      .attr('stroke', '#8C9DB8')
      .attr('stroke-width', 2)
      .attr('d', (d) => {
        const [sx, sy] = radialPoint(d.source.x!, d.source.y!);
        const [tx, ty] = radialPoint(d.target.x!, d.target.y!);
        const dx = tx - sx;
        const dy = ty - sy;
        const len = Math.sqrt(dx * dx + dy * dy);
        const offsetX = (dx / len) * 35;
        const offsetY = (dy / len) * 35;

        return `M${sx + offsetX},${sy + offsetY} L${tx - offsetX},${
          ty - offsetY
        }`;
      });

    // Solid Edges with Link icon in middle
    const solidIconPositions:
      | ((prevState: { x: number; y: number }[]) => { x: number; y: number }[])
      | { x: any; y: any }[] = [];

    g.selectAll('path.solid-link')
      .data(root.links().filter((d) => d.target.data.type === 'solid'))
      .enter()
      .append('path')
      .attr('class', 'solid-link')
      .attr('fill', 'none')
      .attr('stroke', '#8C9DB8')
      .attr('stroke-width', 2)
      .attr('d', (d) => {
        const [sx, sy] = radialPoint(d.source.x!, d.source.y!);
        const [tx, ty] = radialPoint(d.target.x!, d.target.y!);
        const dx = tx - sx;
        const dy = ty - sy;
        const len = Math.sqrt(dx * dx + dy * dy);
        const offsetX = (dx / len) * iconRadius;
        const offsetY = (dy / len) * iconRadius;

        solidIconPositions.push({
          x: (sx + tx) / 2 - 10,
          y: (sy + ty) / 2 - 10,
        });

        return `M${sx + offsetX},${sy + offsetY} L${tx - offsetX},${
          ty - offsetY
        }`;
      });

    setSolidEdgeIcons(solidIconPositions);

    // Red Edges with AlertCriticalOutline icon in middle
    const iconPositions: React.SetStateAction<{ x: number; y: number }[]> = [];

    g.selectAll('path.red-link-icon')
      .data(root.links().filter((d) => d.target.data.type === 'redEdge'))
      .enter()
      .append('path')
      .attr('class', 'red-link-icon')
      .attr('fill', 'none')
      .attr('stroke', '#D32F2F')
      .attr('stroke-width', 2)
      .attr('d', (d) => {
        const [sx, sy] = radialPoint(d.source.x!, d.source.y!);
        const [tx, ty] = radialPoint(d.target.x!, d.target.y!);
        const dx = tx - sx;
        const dy = ty - sy;
        const len = Math.sqrt(dx * dx + dy * dy);
        const offsetX = (dx / len) * iconRadius;
        const offsetY = (dy / len) * iconRadius;

        iconPositions.push({ x: (sx + tx) / 2 - 10, y: (sy + ty) / 2 - 10 });

        return `M${sx + offsetX},${sy + offsetY} L${tx - offsetX},${
          ty - offsetY
        }`;
      });

    setRedEdgeIcons(iconPositions);

    // Animated links
    const animatedLinks = root
      .links()
      .filter((d) => d.target.data.type === 'animated');

    g.selectAll('path.animated-link-bg')
      .data(animatedLinks)
      .enter()
      .append('path')
      .attr('class', 'animated-link-bg')
      .attr('fill', 'none')
      .attr('stroke', '#90ee90')
      .attr('stroke-width', 2)
      .attr('d', (d) => {
        const [sx, sy] = radialPoint(d.source.x!, d.source.y!);
        const [tx, ty] = radialPoint(d.target.x!, d.target.y!);
        const dx = tx - sx;
        const dy = ty - sy;
        const len = Math.sqrt(dx * dx + dy * dy);
        const offsetX = (dx / len) * iconRadius;
        const offsetY = (dy / len) * iconRadius;

        return `M${sx + offsetX},${sy + offsetY} L${tx - offsetX},${
          ty - offsetY
        }`;
      });

    g.selectAll('path.animated-link-fg')
      .data(animatedLinks)
      .enter()
      .append('path')
      .attr('class', 'animated-link-fg')
      .attr('fill', 'none')
      .attr('stroke', '#006400')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '50,20')
      .attr('d', (d) => {
        const [sx, sy] = radialPoint(d.source.x!, d.source.y!);
        const [tx, ty] = radialPoint(d.target.x!, d.target.y!);
        const dx = tx - sx;
        const dy = ty - sy;
        const len = Math.sqrt(dx * dx + dy * dy);
        const offsetX = (dx / len) * iconRadius;
        const offsetY = (dy / len) * iconRadius;

        return `M${sx + offsetX},${sy + offsetY} L${tx - offsetX},${
          ty - offsetY
        }`;
      })
      .each(function () {
        const path = select(this);

        animateLine(path);
      });

    function animateLine(
      path: Selection<SVGPathElement, unknown, null, undefined>
    ) {
      let offset = 0;

      timer(() => {
        offset = (offset + 1) % 70;
        path.attr('stroke-dashoffset', offset);
      });
    }

    const nodesWithPositions = root.descendants().map((d) => {
      const [x, y] = radialPoint(d.x!, d.y!);

      return {
        x: x + width / 2,
        y: y + height - 100,
        data: d.data,
      };
    });

    setNodePositions(nodesWithPositions);
  }, [data, onBack]);

  return (
    <div className='graph-container'>
      <svg ref={svgRef} className='graph-svg' />

      {/* Red Edge Icons */}
      {redEdgeIcons.map((icon) => (
        <div
          className='edge-icon'
          style={{ left: icon.x + 500, top: icon.y + 500 }}>
          <Icon kind='AlertCriticalOutline' color='critical-700' />
        </div>
      ))}

      {/* Solid Link Icons */}
      {solidEdgeIcons.map((icon) => (
        <div
          className='edge-icon'
          style={{ left: icon.x + 500, top: icon.y + 500 }}>
          <Icon kind='Link' color='gray-600' size='small' />
        </div>
      ))}

      {/* Nodes */}
      {nodePositions.map(({ x, y, data: nodeData }, i) => {
        const isRoot = nodeData.id === data.id;
        const size = isRoot ? 64 : 44;
        const isRed = nodeData.type === 'redEdge';
        const isDotted = nodeData.type === 'dotted';
        let backgroundColor = '';

        if (isRoot) {
          backgroundColor = '#426498';
        } else {
          backgroundColor = 'var(--color-background-primary-subtle)';
        }

        const cursorStyle = !isRoot ? 'pointer' : undefined;

        const borderColor = isRed
          ? 'var(--color-background-primary-subtle)'
          : 'var(--color-border-secondary-subtlest)';

        return (
          <div
            key={nodeData.id ?? `node-${i}`}
            style={{
              position: 'absolute',
              left: x - size / 2,
              top: y - size / 2,
              width: size,
              height: size,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
              border: `2px solid ${borderColor}`,
              outline: !isRed ? '2px solid #1976D2' : `2px solid #CC1C10`,
              outlineOffset: '2px',
              borderRadius: '50%',
              background: backgroundColor,
              cursor: cursorStyle,
              zIndex: 3,
              mixBlendMode: isDotted ? 'luminosity' : undefined,
            }}
            tabIndex={isRoot ? -1 : 0}
            onClick={
              isRoot
                ? undefined
                : () => {
                    onNodeClick(nodeData);
                  }
            }
            onKeyDown={
              isRoot
                ? undefined
                : (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onNodeClick(nodeData);
                    }
                  }
            }>
            {nodeData.appName ? (
              <div className='node-app-icon'>
                <ApplicationIcon
                  logo={nodeData.appName}
                  iconColor={isRoot ? '--color-foreground-neutral-inverse' : ''}
                  size='medium'
                />
              </div>
            ) : (
              nodeData.icon && (
                <Icon
                  kind={nodeData.icon}
                  color={isRoot ? '--color-foreground-neutral-inverse' : ''}
                  size='medium'
                />
              )
            )}
          </div>
        );
      })}

      <div
        className='back-button'
        tabIndex={0}
        role='button'
        onClick={onBack}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onBack();
          }
        }}>
        <Icon
          kind='ChevronDown'
          size='xxLarge'
          color='--color-foreground-neutral-bold'
        />
      </div>
    </div>
  );
}

export default SemicircularGraph;
